/**
 * Shared upload chain for a single Magic Sign document: getSignedUrl → uploadFile →
 * saveDocMetaDataInDocService → updateDocMetadata (MU registration) → createMagicSignRecord.
 *
 * Extracted so useMagicSignLogic.tsx (company-scoped) and useGlobalMagicSignLogic.ts (orphan/
 * global upload) call one copy of the chain, parameterized by org-scoping, instead of maintaining
 * two near-identical copies.
 *
 * Pattern reference: useMagicSignLogic.tsx (previous inline implementation).
 */
import { MagicSignRow, MagicSignStatus } from './types';
import { MAGIC_SIGN_UPLOAD_FLOW_ID, MagicSignSource } from './constants';
import { createMagicSignRecord } from './MagicSignService';
import {
    getSignedUrl,
    uploadFile,
    saveDocMetaDataInDocService,
} from '../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { updateDocMetadata } from '../MagicUpload/MagicUploadHelper';

export interface UploadMagicSignDocumentParams {
    file: File;
    pnoId: string;
    /** Company-scoped upload target. Omitted for the global "orphan" upload (no company selected yet). */
    orgId?: string;
    mnemosyneOrgId: string;
    stagingCategoryId?: string;
}

/**
 * Runs the full upload chain for one file and returns the created Magic Sign row.
 * Throws on any step's failure — callers own the per-file try/catch + toast.
 */
export async function uploadMagicSignDocument({
    file,
    pnoId,
    orgId,
    mnemosyneOrgId,
    stagingCategoryId,
}: UploadMagicSignDocumentParams): Promise<MagicSignRow> {
    // Step 1: upload to Mnemosyne — mirrors useMnemosyneUpload exactly. categoryId comes from
    // investor/company config staging bucket; flowId=16 is the platform-wide general-upload flow
    // used by both MagicUpload and MagicSign.
    const signResponse = await getSignedUrl({ file }, { assesseeOrgId: orgId, mnemosyneOrgId, categoryId: stagingCategoryId });
    if (!signResponse?.signedUrl) {
        throw new Error('No signed URL returned');
    }
    await uploadFile(file, signResponse.signedUrl);
    const docData = await saveDocMetaDataInDocService({
        input: {
            categoryId: stagingCategoryId,
            fileName: file.name,
            fileSizeKb: file.size / 1000,
            flowId: MAGIC_SIGN_UPLOAD_FLOW_ID,
            fileType: file.type,
            mnemosyneOrgId,
        },
        orgId,
    });
    const mnemosyneDocId: string = docData?.id ?? '';

    // Step 2: MU registration — POST /magic/update/docMetaData with source=MAGIC_SIGN. This
    // registers the DocStore row that /magic/sign/create requires as a precondition. orgId is
    // omitted (undefined) for the orphan/global upload path, mirroring useMnemosyneUpload's
    // untagged path.
    await new Promise<void>((resolve, reject) => {
        updateDocMetadata(
            {
                docId: mnemosyneDocId,
                fileName: file.name,
                originalFileName: file.name,
                contentType: file.type,
                pnoId,
                orgId,
                source: MagicSignSource.MAGIC_SIGN,
            },
            {
                onSuccess: () => resolve(),
                onError: (msg?: string) => reject(new Error(msg ?? 'MU registration failed')),
            },
        );
    });

    // Step 3: create the Magic Sign record (P2 — /upload is deleted)
    const createRes = await createMagicSignRecord({ mnemosyneDocId });
    return (
        createRes?.data?.data ?? {
            id: mnemosyneDocId,
            fileName: file.name,
            status: MagicSignStatus.UPLOADED,
            mnemosyneDocId,
        }
    );
}
